import { useContext, type HTMLAttributes, type MouseEvent, type ChangeEvent } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { PageContext } from '@contexts/page-context'
import { useShouldTriggerWall as useShouldTriggerGoTemplatesWall } from '@components/section/go-templates-tool/use-should-trigger-wall'
import { useBrowserService, useExperimentService } from '@lib/hooks'

import { ToolInput } from './tool-input'
import { ToolActionRow } from './tool-action-row'
import { toolOnSubmit, useToolApi, createWallGuard } from '../submit-handler'
import { useTool } from '../tool-context'
import { ToolFormValues } from '../types'
import { analyzeTextAndMaybeTruncateToDesiredWordLimit } from '../utils'
import { wallGuardTracking } from '../tracking'

import styles from '../tool.module.scss'

export type ToolFormProps = HTMLAttributes<HTMLFormElement>

export function ToolForm({ ...rest }: ToolFormProps) {
  const {
    toolType,
    toolInputName,
    setOutputContent,
    setNextBestActionCopy,
    llmPrompt,
    setIsLoading,
    setToolError,
    triggerUsageWall,
    triggerTextLimitWall,
    toolUsageTracker,
    maxWordLimit,
    sampleText,
    isEligibleForToolsGoogleOneTap,
    setShowToolsGoogleOneTap,
  } = useTool()

  const methods = useForm<ToolFormValues>({
    defaultValues: { [toolInputName]: '' },
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  })

  const { pageId, slug } = useContext(PageContext)
  const { isMobileOrTablet } = useBrowserService()
  const { getToolsGoogleOneTapAssignment } = useExperimentService()
  const { handleSubmit, watch } = methods

  const text = watch(toolInputName)
  const wordCount = analyzeTextAndMaybeTruncateToDesiredWordLimit(text).words
  const shouldTriggerTextLimitWall = wordCount > maxWordLimit

  const toolApi = useToolApi(toolType, toolInputName, setOutputContent, setNextBestActionCopy, llmPrompt)
  const shouldTriggerGoTemplatesWall = useShouldTriggerGoTemplatesWall()

  const wallGuard = createWallGuard({
    shouldTriggerTextLimitWall,
    triggerTextLimitWall,
    shouldTriggerGoTemplatesWall,
    triggerUsageWall,
    toolUsageTracker,
  })

  const onFormChangeCapture = (event: ChangeEvent<HTMLFormElement>) => {
    const wallGuardResult = wallGuard()
    if (wallGuardResult.shouldWall && wallGuardResult.wallToTrigger !== 'textLimit') {
      event.stopPropagation()

      wallGuardResult.wallTrigger()
      wallGuardTracking(wallGuardResult, pageId, slug)
    }
  }

  const isGoogleOneTapOnTaskComplete = () => {
    return isEligibleForToolsGoogleOneTap && getToolsGoogleOneTapAssignment(isMobileOrTablet) === 'task_complete'
  }

  const onFormClickCapture = (event: MouseEvent<HTMLFormElement>) => {
    const formInputTagNames = new Set(['BUTTON', 'INPUT', 'TEXTAREA', 'SELECT'])
    const { target } = event

    // Type guard: check if target is an HTMLElement
    const isTargetFormInput =
      (target instanceof HTMLElement && formInputTagNames.has(target.tagName)) ||
      (target instanceof HTMLElement && target.parentElement && formInputTagNames.has(target.parentElement.tagName))

    if (isTargetFormInput) {
      const wallGuardResult = wallGuard()
      if (wallGuardResult.shouldWall && wallGuardResult.wallToTrigger !== 'textLimit') {
        event.preventDefault()

        wallGuardResult.wallTrigger()
        wallGuardTracking(wallGuardResult, pageId, slug)
      }
    }
  }

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={handleSubmit(
          toolOnSubmit({
            toolType,
            toolApi,
            wallGuard,
            setIsLoading,
            setToolError,
            pageId,
            slug,
            toolUsageTracker,
            sampleText,
            toolInputName,
            setShowToolsGoogleOneTap,
            isGoogleOneTapOnTaskComplete,
          })
        )}
        method="post"
        className={styles.toolFormContainer}
        onChangeCapture={onFormChangeCapture}
        onClickCapture={onFormClickCapture}
        {...rest}
      >
        <ToolInput />
        <ToolActionRow />
      </form>
    </FormProvider>
  )
}
